import React, {
    useContext,
    useEffect,
    useState,
    useCallback,
    createContext,
} from "react";
import { useAuth } from "hooks/Auth";
import { apiGet } from "helpers/Request";
import { transform as issueTransformer } from "schemas/Issue";
import { getPaginationMeta } from "schemas/Util";
import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import { useLocation } from "react-router-dom";
import Pagination from "components/General/Pagination";
import { AppContext } from "context/App";
import IssueTable from "./IssueTable";
import IssueListToolbar from "./IssueListToolbar";
import { useHistory } from "react-router-dom";
import ButtonGroup from "components/Buttons/ButtonGroup";

export const IssueListContext = createContext();

export default function IssueScreen(props) {
    const auth = useAuth();
    const location = useLocation();
    const history = useHistory();
    const { setCurrentNav } = useContext(AppContext);

    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [paginationMeta, setPaginationMeta] = useState({});
    const [queryParams, setQueryParams] = useState(
        new URLSearchParams(location.search)
    );

    const setSelected = (e, id) => {
        if (e.target.checked) {
            setSelectedItems([...selectedItems, id]);
        } else {
            setSelectedItems(selectedItems.filter((item) => item !== id));
        }
    };

    const updateParams = (key, val = null) => {
        const params = new URLSearchParams(location.search);

        if (typeof key === "object") {
            Object.entries(key).forEach(([k, v]) => {
                if (v === null) {
                    params.delete(k);
                    return;
                }
                params.set(k, v);
            });
        } else {
            if (params.has(key) && params.get(key) === val) {
                params.delete(key);
            } else {
                params.set(key, val);
            }
        }

        history.replace(location.pathname + "?" + params.toString());
    };

    const onResetFilters = () => {
        history.replace(location.pathname + "?");
    };

    const load = useCallback(() => {
        setLoading(true);
        setItems([]);
        setSelectedItems([]);
        const defaultParams = {
            per_page: 40,
            dir: "desc",
            order: "published_at",
        };
        const definedParams = Object.fromEntries(
            new URLSearchParams(location.search)
        );

        apiGet(
            "/v4/issue",
            auth.publication,
            Object.assign({}, defaultParams, definedParams)
        ).then((res) => {
            setItems(issueTransformer(res));
            setPaginationMeta(getPaginationMeta(res));
            setLoading(false);
        });
    }, [location, auth.publication]);

    useEffect(() => {
        load();
        setQueryParams(new URLSearchParams(location.search));
    }, [location, load]);

    useEffect(() => {
        setCurrentNav("planner");
    }, [setCurrentNav]);

    return (
        <MainBlock>
            <IssueListContext.Provider
                value={{ items, selectedItems, loading, load }}
            >
                <IssueListToolbar queryParams={queryParams} />
                <div className="my-4 flex flex-wrap justify-start gap-2">
                    <div className="order-last md:order-first">
                        <ButtonGroup
                            variant="xs"
                            buttons={[
                                <button
                                    type="button"
                                    key="reset-filters"
                                    onClick={onResetFilters}
                                >
                                    Reset
                                </button>,
                            ]}
                        />
                    </div>
                    <div>
                        <ButtonGroup
                            variant="xs"
                            buttons={[
                                <button
                                    type="button"
                                    key="filter-view-table"
                                    selected={
                                        parseInt(
                                            queryParams.get("include_archived")
                                        ) !== 1
                                    }
                                    onClick={() =>
                                        updateParams("include_archived", 0)
                                    }
                                >
                                    Active Only
                                </button>,
                                <button
                                    type="button"
                                    key="filter-view-table"
                                    selected={
                                        parseInt(
                                            queryParams.get("include_archived")
                                        ) === 1
                                    }
                                    onClick={() =>
                                        updateParams("include_archived", 1)
                                    }
                                >
                                    All Issues
                                </button>,
                            ]}
                        />
                    </div>
                </div>
                <IssueTable
                    addSelected={setSelected}
                    setAllSelected={setSelectedItems}
                    queryParams={queryParams}
                />
            </IssueListContext.Provider>

            <div className="my-4">
                <Pagination
                    basePath="custom-content/channel"
                    {...paginationMeta}
                />
            </div>
        </MainBlock>
    );
}
